/**
 * Erkennt aus den vorhandenen Buchungen wiederkehrende Zahlungen und schlaegt
 * daraus Dauerauftraege vor. Kriterium: DERSELBE Empfaenger mit DEMSELBEN FESTEN
 * BETRAG taucht in mindestens drei AUFEINANDERFOLGENDEN Monaten auf. Der Betrag
 * ist Teil des Schluessels, damit z. B. mehrere PayPal-Abos (16 € Disney, 10 € RTL)
 * als getrennte Daueraufträge erkannt werden – bei PayPal ist der Empfaenger stets
 * „PayPal", nur der Betrag unterscheidet die Abos.
 * Reine Logik ohne Redux/React, damit die Vorschlags-Ansicht sie direkt nutzt.
 */

import { monatSchluessel, verschiebeMonat, betragVon } from './berechnung';

// Mindestlaenge einer luecklosen Monatsserie (> 2 Wiederholungen).
const MIN_SERIE = 3;

/**
 * Normalisiert Notiz/Empfaenger zu einer stabilen Signatur – Teil des
 * Verknuepfungsschluessels zwischen Buchung und Dauerauftrag. Bewusst aus dem
 * ROH-Empfaenger (nicht der ggf. umbenannten Anzeige-Notiz) berechnet.
 */
export const signaturVon = (notiz) =>
  (notiz || '')
    .toLowerCase()
    .replace(/[0-9]+/g, ' ')
    .replace(/[^a-zäöüß ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Signatur eines gespeicherten Dauerauftrags (Feld `signatur`, sonst aus Notiz). */
const dauerSignatur = (d) => d.signatur || signaturVon(d.notiz);

/** Zwei Betraege gelten als gleich, wenn sie sich um weniger als einen halben Cent unterscheiden. */
const betragGleich = (a, b) => Math.abs(Number(a) - Number(b)) < 0.005;

/** Gruppen-/Verknuepfungsschluessel aus Typ, Empfaenger-Signatur und festem Betrag. */
const schluessel = (typ, signatur, betrag) => `${typ}|${signatur}|${Number(betrag).toFixed(2)}`;

/** True, wenn eine Buchung zu einem (aktiven) Dauerauftrag passt: Typ + Signatur + fester Betrag. */
export const passtZuDauerauftrag = (buchung, dauerauftrag) =>
  !dauerauftrag.abgelehnt &&
  buchung.typ === dauerauftrag.typ &&
  signaturVon(buchung.notiz) === dauerSignatur(dauerauftrag) &&
  betragGleich(betragVon(buchung), dauerauftrag.betrag);

/**
 * Findet den zu einer Buchung passenden gespeicherten Dauerauftrag: bereits
 * verknuepfte per serienId, sonst per Typ + Signatur + Betrag. Null, wenn keiner passt.
 */
export const findeDauerauftragFuer = (buchung, dauerbuchungen = []) => {
  if (buchung.serienId) return dauerbuchungen.find((d) => d.uuid === buchung.serienId) || null;
  if (!signaturVon(buchung.notiz)) return null;
  return dauerbuchungen.find((d) => passtZuDauerauftrag(buchung, d)) || null;
};

/** Haeufigster Wert einer Liste (erste Fundstelle bei Gleichstand). */
const haeufigster = (werte) => {
  const zaehler = new Map();
  let best = werte[0];
  let max = 0;
  werte.forEach((w) => {
    const n = (zaehler.get(w) || 0) + 1;
    zaehler.set(w, n);
    if (n > max) {
      max = n;
      best = w;
    }
  });
  return best;
};

/** Laenge der laengsten lueckenlosen Kette aufeinanderfolgender Monate. */
const laengsteMonatsserie = (monate) => {
  const sortiert = [...new Set(monate)].sort();
  let best = sortiert.length ? 1 : 0;
  let lauf = best;
  for (let i = 1; i < sortiert.length; i += 1) {
    lauf = verschiebeMonat(sortiert[i - 1], 1) === sortiert[i] ? lauf + 1 : 1;
    if (lauf > best) best = lauf;
  }
  return best;
};

/** Buchungen mit erkennbarem Empfaenger, die nicht schon aus einem Dauerauftrag stammen. */
const kandidaten = (buchungen) =>
  buchungen.filter((b) => !b.serienId && signaturVon(b.notiz).length >= 3);

/** Gruppiert die Kandidaten nach Typ + Empfaenger-Signatur + festem Betrag. */
const gruppiere = (buchungen) => {
  const gruppen = new Map();
  kandidaten(buchungen).forEach((b) => {
    const key = schluessel(b.typ, signaturVon(b.notiz), betragVon(b));
    if (!gruppen.has(key)) gruppen.set(key, []);
    gruppen.get(key).push(b);
  });
  return gruppen;
};

/** Baut aus einer Buchungsgruppe (gleicher Empfaenger + Betrag) einen Dauerauftrag-Vorschlag. */
const baueVorschlag = (key, gruppe) => ({
  key,
  signatur: signaturVon(gruppe[0].notiz),
  typ: gruppe[0].typ,
  betrag: betragVon(gruppe[0]),
  kategorie: haeufigster(gruppe.map((b) => b.kategorie)),
  zahlweg: haeufigster(gruppe.map((b) => b.zahlweg)),
  ort: haeufigster(gruppe.map((b) => b.ort || null)),
  notiz: haeufigster(gruppe.map((b) => b.notiz)),
  anzahl: gruppe.length,
});

/** Schluessel, die bereits durch einen gespeicherten Dauerauftrag abgedeckt sind. */
const abgedeckteSchluessel = (dauerbuchungen) =>
  new Set(dauerbuchungen.map((d) => schluessel(d.typ, dauerSignatur(d), d.betrag)));

/**
 * Ermittelt Dauerauftrag-Vorschlaege aus den Buchungen. Bereits entschiedene
 * (bestaetigte oder abgelehnte) Empfaenger+Betrag-Kombinationen werden ausgelassen.
 * Sortiert nach Haeufigkeit (absteigend).
 */
export const erkenneDauerauftraege = (buchungen, dauerbuchungen = []) => {
  const abgedeckt = abgedeckteSchluessel(dauerbuchungen);
  const vorschlaege = [];
  gruppiere(buchungen).forEach((gruppe, key) => {
    if (abgedeckt.has(key)) return;
    const monate = gruppe.map((b) => monatSchluessel(b.datum));
    if (laengsteMonatsserie(monate) < MIN_SERIE) return;
    vorschlaege.push(baueVorschlag(key, gruppe));
  });
  return vorschlaege.sort((a, b) => b.anzahl - a.anzahl);
};
